import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import type { CorrectionDesignationsDto, CorrectionRequestsDto, CorrectionWorkflowMeDto, PaymentCorrectionStatus, SetCorrectionDesignationsInput } from '@nexao/contracts';
import {
  getCorrectionDesignations,
  getCorrectionWorkflowMe,
  listCorrectionRequests,
  setCorrectionDesignations,
} from '../services/correction-workflow.service';

/** Every query of the workflow hangs under one key, so a decision refreshes the list, the badge and the screen's own view together. */
export const CORRECTION_WORKFLOW_KEY = ['correction-workflow'] as const;

export function useCorrectionWorkflowMe(): UseQueryResult<CorrectionWorkflowMeDto> {
  return useQuery({ queryKey: [...CORRECTION_WORKFLOW_KEY, 'me'], queryFn: getCorrectionWorkflowMe, staleTime: 30_000 });
}

export function useCorrectionDesignations(): UseQueryResult<CorrectionDesignationsDto> {
  return useQuery({ queryKey: [...CORRECTION_WORKFLOW_KEY, 'designations'], queryFn: getCorrectionDesignations });
}

export function useSaveCorrectionDesignations(): UseMutationResult<CorrectionDesignationsDto, unknown, SetCorrectionDesignationsInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: setCorrectionDesignations,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: CORRECTION_WORKFLOW_KEY }),
  });
}

export function useCorrectionRequests(query: { status?: PaymentCorrectionStatus; mine?: boolean }, enabled = true): UseQueryResult<CorrectionRequestsDto> {
  return useQuery({
    queryKey: [...CORRECTION_WORKFLOW_KEY, 'requests', query.status ?? 'all', query.mine ? 'mine' : 'everyone'],
    queryFn: () => listCorrectionRequests(query),
    enabled,
  });
}
